import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import { consumeNavFeedback } from '@/composables/useDashboardNavFeedback'

/**
 * Runs async dashboard page setup on mount and exposes a loading flag for skeleton UI.
 * Skips the skeleton when auth has already been fetched (instant revisit); still refreshes in the background.
 */
export function useDashboardPageLoad(loader: () => Promise<void>) {
  const store = useStore()
  const needsSkeleton = store.getters.requestLoading
  const isPageLoading = ref(needsSkeleton)

  onMounted(async () => {
    consumeNavFeedback()
    if (needsSkeleton) {
      isPageLoading.value = true
    }
    try {
      await loader()
    } finally {
      isPageLoading.value = false
      store.commit('SET_LOADING', false)
    }
  })

  return { isPageLoading }
}

/** Common loader: authorisation + profile when not already in Vuex. */
export async function loadAuthorisationPageData(
  store: {
    dispatch: (action: string) => Promise<unknown>
    getters: { requestLoading: boolean; user: { emailAddress?: string } | null }
  },
  extra?: () => Promise<void>
): Promise<void> {
  if (store.getters.requestLoading) {
    await store.dispatch('fetchAuth')
  } else {
    void store.dispatch('fetchAuth')
  }

  if (isEmpty(store.getters.user?.emailAddress)) {
    await store.dispatch('fetchUser')
  }

  if (extra) {
    await extra()
  }
}
